import {
  Container,
  Stack,
  Box,
  Text,
  Heading,
  Divider,
  HStack,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import t from "../translations";

const MotionBox = motion(Box);

function ResourceCard({ entry, color, index }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.400");

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
      whileHover={{ y: -4 }}
      style={{ borderRadius: "var(--chakra-radii-xl)" }}
    >
      <Card
        bg={cardBg}
        borderRadius="xl"
        _hover={{ boxShadow: "xl" }}
        transition="box-shadow 0.25s ease"
      >
        <CardBody>
          <Stack spacing={3}>
            <HStack justify="space-between" flexWrap="wrap" gap={2}>
              <Heading size="sm" color={`${color}.400`}>
                {entry.title}
              </Heading>
              {entry.link && (
                <Button
                  as="a"
                  href={entry.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  leftIcon={<ExternalLinkIcon />}
                  variant="outline"
                  colorScheme={color}
                  flexShrink={0}
                >
                  {t.resources.visitButton}
                </Button>
              )}
            </HStack>
            <Text color={textColor} fontSize="sm" lineHeight={1.8}>
              {entry.body}
            </Text>
          </Stack>
        </CardBody>
      </Card>
    </motion.div>
  );
}

export default function Resources({ color = "purple" }) {
  const textColor = useColorModeValue("gray.600", "gray.400");
  const tabBg = useColorModeValue("gray.100", "gray.700");
  const selectedBg = useColorModeValue("white", "gray.800");

  useEffect(() => {
    document.title = t.resources.pageTitle;
  }, []);

  return (
    <Container maxW="4xl" pt={{ base: 20, md: 28 }} pb={{ base: 20, md: 36 }}>
      <Stack spacing={{ base: 8, md: 10 }}>
        <Stack align="center" direction="row" px={4}>
          <HStack mx={4}>
            <Text fontWeight={800} fontSize={{ base: "xl", md: "2xl" }} whiteSpace="nowrap">
              {t.resources.heading}
            </Text>
          </HStack>
          <Divider />
        </Stack>

        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          px={4}
        >
          <Text color={textColor} fontSize="lg" lineHeight={1.9}>
            {t.resources.intro}
          </Text>
        </MotionBox>

        <Tabs variant="unstyled" isLazy>
          <TabList justifyContent="center" gap={2} px={{ base: 2, md: 4 }} flexWrap="wrap">
            {t.resources.categories.map((cat) => (
              <Tab
                key={cat.label}
                fontWeight={600}
                fontSize={{ base: "xs", md: "sm" }}
                px={{ base: 3, md: 6 }}
                py={2}
                borderRadius="full"
                bg={tabBg}
                color="gray.500"
                _selected={{
                  bg: selectedBg,
                  color: `${cat.color}.400`,
                  boxShadow: "md",
                }}
                transition="all 0.2s"
              >
                {cat.label}
              </Tab>
            ))}
          </TabList>

          <TabPanels mt={8}>
            {t.resources.categories.map((cat) => (
              <TabPanel key={cat.label} px={0}>
                <Stack px={4} spacing={4}>
                  {cat.entries.map((entry, i) => (
                    <ResourceCard
                      key={entry.title}
                      entry={entry}
                      color={cat.color}
                      index={i}
                    />
                  ))}
                </Stack>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Stack>
    </Container>
  );
}
